import { type FormEvent, useEffect, useRef, useState } from "react";
import {
  ApiError,
  type Category,
  type CategoryEntry,
  type CategoryGroup,
  formatMoney,
  type CategorySuggestion,
  type Direction,
  api,
  directionLabel,
} from "./api";
import { SelectField } from "./fields";
import { PageHeader } from "./layout/Layout";
import { Actions, Alert, Badge, Box, Empty, Field, IconAction, Loading, Modal, Table, Th, Working } from "./ui";

const DIRECTIONS: readonly Direction[] = ["money_in", "money_out", "stock"];
/**
 * The sides a category can be added on. Inventory movements were never used by anything
 * (D, 15 October 2026: "do you think that it is useless here? Can you allow me to delete?"):
 * the ones there can be deleted, and no new one is added.
 */
const ADDABLE: readonly Direction[] = ["money_in", "money_out"];

/**
 * Categories (quotation A7).
 *
 * Staff add these by typing. The only thing standing between that and a list holding
 * Meal, Meals and meals is the check underneath: a name close to one that already
 * exists comes back as a question, not a second row.
 */
export function Categories(): React.ReactElement {
  const [categories, setCategories] = useState<Category[]>([]);
  /** Headings over categories (D, 15 October 2026: Inventory Expenses, General Expenses). */
  const [groups, setGroups] = useState<CategoryGroup[]>([]);
  const [editingGroup, setEditingGroup] = useState<CategoryGroup | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  /** The category being renamed, moved, switched off or deleted. */
  const [editing, setEditing] = useState<Category | undefined>();

  async function reload(): Promise<void> {
    try {
      const [result, grouped] = await Promise.all([api.categories(), api.categoryGroups()]);
      setCategories(result.categories);
      setGroups(grouped.groups);
      setError(undefined);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not load transaction categories");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function move(work: () => Promise<unknown>): Promise<void> {
    try {
      await work();
      await reload();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not move it");
    }
  }

  if (loading) return <Loading />;

  return (
    <>
      <PageHeader
        title="Transaction categories"
        subtitle="Classify financial and inventory movements. Any user can add a category."
      />

      {error ? <Alert tone="danger">{error}</Alert> : null}

      <div className="grid grid-cols-12 gap-x-6">
        <div className="xl:col-span-4 col-span-12">
          <AddCategory groups={groups} onAdded={() => void reload()} />
          <AddGroup onAdded={() => void reload()} />
        </div>

        <div className="xl:col-span-8 col-span-12">
          {categories.length === 0 ? (
            <Box title="Transaction categories">
              <Empty icon="ri-price-tag-3-line">No transaction categories have been created.</Empty>
            </Box>
          ) : (
            DIRECTIONS.map((direction) => {
              const inGroup = categories.filter((category) => category.direction === direction);
              const headings = groups.filter((group) => group.direction === direction);
              if (inGroup.length === 0 && headings.length === 0) return null;
              // Each group with its categories under it, then any not in a group.
              const sections = [
                ...headings.map((group) => ({ group, rows: inGroup.filter((category) => category.groupId === group.id) })),
                { group: undefined, rows: inGroup.filter((category) => !category.groupId || !headings.some((group) => group.id === category.groupId)) },
              ].filter((section) => section.group || section.rows.length > 0);

              return (
                <Box
                  key={direction}
                  title={directionLabel(direction)}
                  count={inGroup.length}
                >
                  <Table
                    head={
                      <>
                        <Th>Category name</Th>
                        <Th end>Transactions using this category</Th>
                        <Th end>Amount S$</Th>
                        <Th end>Status</Th>
                        <Th end>Action</Th>
                      </>
                    }
                  >
                    {sections.map((section) => [
                      headings.length > 0 ? (
                        <tr key={`group-${section.group?.id ?? "none"}`} className="border-b border-defaultborder bg-light dark:bg-black/20">
                          <td colSpan={4} className="!py-2">
                            <span className="font-semibold text-[0.9375rem]">{section.group?.name ?? "Not in a group"}</span>
                            {section.group?.forResale ? <span className="ms-2"><Badge tone="primary">Shows on Inventory purchases</Badge></span> : null}
                            {section.group && !section.group.forResale && direction === "money_out" ? (
                              <span className="ms-2 text-[0.75rem] text-[#8c9097] dark:text-white/50">Shows on Expenses</span>
                            ) : null}
                          </td>
                          <td className="!py-2">
                            {section.group ? (
                              <Actions>
                                {/* In the order D wants them, as a statement reads (D, 15 October 2026). */}
                                <IconAction icon="ri-arrow-up-line" label={`Move ${section.group.name} up`} tone="secondary" onClick={() => void move(() => api.moveCategoryGroup(section.group!.id, -1))} />
                                <IconAction icon="ri-arrow-down-line" label={`Move ${section.group.name} down`} tone="secondary" onClick={() => void move(() => api.moveCategoryGroup(section.group!.id, 1))} />
                                <IconAction icon="ri-edit-line" label={`Rename or change ${section.group.name}`} tone="primary" onClick={() => setEditingGroup(section.group)} />
                              </Actions>
                            ) : null}
                          </td>
                        </tr>
                      ) : null,
                      ...section.rows.map((category) => (
                      <tr key={category.id} className="border-b border-defaultborder">
                        <td className={`font-semibold ${headings.length > 0 ? "!ps-6" : ""}`}>{category.name}</td>
                        <td className="!text-end text-[#8c9097] dark:text-white/50">
                          <EntriesPeek category={category} />
                        </td>
                        <td className="!text-end font-semibold tabular-nums">{formatMoney(category.amountCents ?? 0)}</td>
                        <td className="!text-end">
                          {category.isActive ? <Badge tone="success">Active</Badge> : <Badge tone="light">Inactive</Badge>}
                        </td>
                        <td>
                          <Actions>
                            <IconAction icon="ri-arrow-up-line" label={`Move ${category.name} up`} tone="secondary" onClick={() => void move(() => api.moveCategory(category.id, -1))} />
                            <IconAction icon="ri-arrow-down-line" label={`Move ${category.name} down`} tone="secondary" onClick={() => void move(() => api.moveCategory(category.id, 1))} />
                            <IconAction icon="ri-edit-line" label="Edit or delete" tone="primary" onClick={() => setEditing(category)} />
                          </Actions>
                        </td>
                      </tr>
                      )),
                    ])}
                  </Table>
                </Box>
              );
            })
          )}
        </div>
      </div>

      {editingGroup ? (
        <EditGroup
          group={editingGroup}
          onClose={() => setEditingGroup(undefined)}
          onDone={() => {
            setEditingGroup(undefined);
            void reload();
          }}
        />
      ) : null}
      {editing ? (
        <EditCategory
          groups={groups}
          category={editing}
          onClose={() => setEditing(undefined)}
          onDone={() => {
            setEditing(undefined);
            void reload();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Rename, move to the other side, switch off, or delete (D, 5 October 2026). One that
 * transactions are filed under keeps its side and can only be switched off, which the
 * server says in words if it is tried.
 */
function EditCategory({
  groups,
  category,
  onClose,
  onDone,
}: {
  readonly groups: readonly CategoryGroup[];
  readonly category: Category;
  readonly onClose: () => void;
  readonly onDone: () => void;
}): React.ReactElement {
  const [name, setName] = useState(category.name);
  const [direction, setDirection] = useState<Direction>(category.direction);
  const [groupId, setGroupId] = useState(category.groupId ?? "");
  const [covers, setCovers] = useState(category.productCategory ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const used = category.useCount > 0;
  const choices = groups.filter((group) => group.direction === direction);

  async function run(work: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      await work();
      onDone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "The change could not be saved");
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title="Edit category"
      onClose={onClose}
      footer={
        <>
          {used ? (
            <button
              type="button"
              className="ti-btn ti-btn-light me-auto"
              disabled={busy}
              onClick={() => void run(() => api.changeCategory(category.id, { isActive: !category.isActive }))}
            >
              {category.isActive ? "Switch off" : "Switch on"}
            </button>
          ) : (
            <button
              type="button"
              className="ti-btn ti-btn-danger me-auto"
              disabled={busy}
              onClick={() => {
                if (window.confirm(`Delete ${category.name}?`)) void run(() => api.deleteCategory(category.id));
              }}
            >
              Delete
            </button>
          )}
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-primary-full"
            disabled={busy || !name.trim()}
            onClick={() =>
              void run(() =>
                api.changeCategory(category.id, {
                  name: name.trim(),
                  direction,
                  groupId: choices.some((group) => group.id === groupId) ? groupId : null,
                  ...(direction === "money_out" ? { productCategory: covers || null } : {}),
                }),
              )
            }
          >
            {busy ? <Working label="Saving" /> : "Save"}
          </button>
        </>
      }
    >
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <div className="grid grid-cols-12 gap-4">
        <Field id="edit-category-name" label="Category name" className="col-span-12">
          <input
            id="edit-category-name"
            type="text"
            className="form-control"
            maxLength={60}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field
          id="edit-category-type"
          label="Transaction type"
          className="col-span-12"
          hint={used ? `Used by ${category.useCount} ${category.useCount === 1 ? "transaction" : "transactions"}, so the type stays as it is.` : undefined}
        >
          {used ? (
            <input id="edit-category-type" type="text" className="form-control" value={directionLabel(direction)} disabled />
          ) : (
            <SelectField<Direction>
              id="edit-category-type"
              value={direction}
              options={DIRECTIONS.filter((entry) => ADDABLE.includes(entry) || entry === category.direction).map((entry) => ({ value: entry, label: directionLabel(entry) }))}
              onChange={setDirection}
            />
          )}
        </Field>
        {choices.length > 0 ? (
          <Field id="edit-category-group" label="Group" className="col-span-12">
            <SelectField
              id="edit-category-group"
              value={choices.some((group) => group.id === groupId) ? groupId : ""}
              options={[{ value: "", label: "Not in a group" }, ...choices.map((group) => ({ value: group.id, label: group.name }))]}
              onChange={setGroupId}
            />
          </Field>
        ) : null}
        {direction === "money_out" ? (
          <Field
            id="edit-category-covers"
            label="Stock it covers"
            className="col-span-12"
            hint="A supplier purchase of this stock shows this category beside its voucher, and counts in its amount. For example Sealed Product Purchases covers Sealed Products."
          >
            <SelectField
              id="edit-category-covers"
              value={covers}
              options={[
                { value: "", label: "None" },
                ...["Sealed Products", "Singles", "Slabs", "Accessories"].map((name) => ({ value: name, label: name })),
              ]}
              onChange={setCovers}
            />
          </Field>
        ) : null}
      </div>
    </Modal>
  );
}

function AddCategory({ groups, onAdded }: { readonly groups: readonly CategoryGroup[]; readonly onAdded: () => void }): React.ReactElement {
  const [name, setName] = useState("");
  const [direction, setDirection] = useState<Direction>("money_out");
  const [groupId, setGroupId] = useState("");
  const choices = groups.filter((group) => group.direction === direction);
  const chosenGroup = choices.some((group) => group.id === groupId) ? groupId : "";
  const [suggestions, setSuggestions] = useState<CategorySuggestion[] | undefined>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [added, setAdded] = useState<string | undefined>();

  async function save(evenIfSimilar: boolean): Promise<void> {
    setError(undefined);
    setAdded(undefined);
    setBusy(true);
    try {
      const result = await api.addCategory(name.trim(), direction, evenIfSimilar, chosenGroup || null);
      if (result.suggestions && result.suggestions.length > 0) {
        setSuggestions(result.suggestions);
      } else {
        setAdded(result.category?.name);
        setName("");
        setSuggestions(undefined);
        onAdded();
      }
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not add the category");
    } finally {
      setBusy(false);
    }
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    void save(false);
  }

  return (
    <Box title="Add category">
      <form className="grid grid-cols-12 gap-4" onSubmit={submit}>
        {error ? (
          <div className="col-span-12">
            <Alert tone="danger">{error}</Alert>
          </div>
        ) : null}
        {added ? (
          <div className="col-span-12">
            <Alert tone="success" onClose={() => setAdded(undefined)}>
              Added {added}.
            </Alert>
          </div>
        ) : null}

        <Field
          id="category-name"
          label="Category name"
          className="col-span-12"
          hint="If a similar category exists, the system shows it first."
        >
          <input
            id="category-name"
            type="text"
            className="form-control"
            value={name}
            placeholder="Event booth rental"
            onChange={(event) => {
              setName(event.target.value);
              setSuggestions(undefined);
            }}
          />
        </Field>

        <Field id="category-direction" label="Transaction type" className="col-span-12">
          <SelectField<Direction>
            id="category-direction"
            value={direction}
            options={[
              { value: "money_out", label: "Funds outgoing" },
              { value: "money_in", label: "Funds incoming" },
            ]}
            onChange={(choice) => {
              setDirection(choice);
              setSuggestions(undefined);
            }}
          />
        </Field>

        {choices.length > 0 ? (
          <Field id="category-group" label="Group" className="col-span-12" hint="The heading it goes under. Add a new group below.">
            <SelectField
              id="category-group"
              value={chosenGroup}
              options={[{ value: "", label: "Not in a group" }, ...choices.map((group) => ({ value: group.id, label: group.name }))]}
              onChange={setGroupId}
            />
          </Field>
        ) : null}

        {suggestions ? (
          <div className="col-span-12">
            <Alert tone="warning">
              <span className="font-semibold">
                {suggestions.length === 1 ? "A similar category already exists: " : "Similar categories already exist: "}
                {suggestions.map((entry) => entry.name).join(", ")}.
              </span>{" "}
              Use the existing category if it is the same, or add yours anyway.
            </Alert>
          </div>
        ) : null}

        <div className="col-span-12 flex flex-wrap gap-2">
          {suggestions ? (
            <>
              <button
                type="button"
                className="ti-btn ti-btn-light !mb-0"
                onClick={() => {
                  setSuggestions(undefined);
                  setName("");
                }}
              >
                Use existing category
              </button>
              <button
                type="button"
                className="ti-btn ti-btn-warning-full !mb-0"
                disabled={busy}
                onClick={() => void save(true)}
              >
                Add {name.trim()} anyway
              </button>
            </>
          ) : (
            <button type="submit" className="ti-btn ti-btn-primary-full !mb-0" disabled={busy || name.trim() === ""}>
              {busy ? <Working label="Checking" /> : "Add category"}
            </button>
          )}
        </div>
      </form>
    </Box>
  );
}

/**
 * A heading over categories (D, 15 October 2026: "maybe instead of Funds paid out: Inventory
 * Expenses, General Expenses, etc, and then I can add the sub category below them"). A group
 * of money paid out can be stock bought to sell: what is filed under it shows on Inventory
 * purchases, everything else on Expenses.
 */
function AddGroup({ onAdded }: { readonly onAdded: () => void }): React.ReactElement {
  const [name, setName] = useState("");
  const [direction, setDirection] = useState<Direction>("money_out");
  const [forResale, setForResale] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [added, setAdded] = useState<string | undefined>();

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    setAdded(undefined);
    try {
      const { group } = await api.addCategoryGroup(name.trim(), direction, direction === "money_out" && forResale);
      setAdded(group.name);
      setName("");
      setForResale(false);
      onAdded();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not add the group");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box title="Add group">
      <form className="grid grid-cols-12 gap-4" onSubmit={(event) => void save(event)}>
        {error ? (
          <div className="col-span-12">
            <Alert tone="danger">{error}</Alert>
          </div>
        ) : null}
        {added ? (
          <div className="col-span-12">
            <Alert tone="success" onClose={() => setAdded(undefined)}>
              Added {added}. Put categories under it with Edit, or pick it when adding one.
            </Alert>
          </div>
        ) : null}
        <Field id="group-name" label="Group name" className="col-span-12">
          <input id="group-name" type="text" className="form-control" maxLength={60} value={name} placeholder="Tradeshow Expenses" onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field id="group-direction" label="Transaction type" className="col-span-12">
          <SelectField<Direction>
            id="group-direction"
            value={direction}
            options={ADDABLE.map((entry) => ({ value: entry, label: directionLabel(entry) }))}
            onChange={setDirection}
          />
        </Field>
        {direction === "money_out" ? (
          <label className="col-span-12 flex items-start gap-2 text-[0.8125rem]">
            <input type="checkbox" className="form-check-input mt-0.5" checked={forResale} onChange={(event) => setForResale(event.target.checked)} />
            <span>Stock bought to sell. Purchases under these categories show on Inventory purchases, not on Expenses.</span>
          </label>
        ) : null}
        <div className="col-span-12">
          <button type="submit" className="ti-btn ti-btn-primary-full !mb-0" disabled={busy || !name.trim()}>
            {busy ? <Working label="Adding" /> : "Add group"}
          </button>
        </div>
      </form>
    </Box>
  );
}

function EditGroup({
  group,
  onClose,
  onDone,
}: {
  readonly group: CategoryGroup;
  readonly onClose: () => void;
  readonly onDone: () => void;
}): React.ReactElement {
  const [name, setName] = useState(group.name);
  const [forResale, setForResale] = useState(group.forResale);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function run(work: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      await work();
      onDone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "The change could not be saved");
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title="Edit group"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="ti-btn ti-btn-danger me-auto"
            disabled={busy}
            onClick={() => {
              if (window.confirm(`Delete the group ${group.name}?`)) void run(() => api.deleteCategoryGroup(group.id));
            }}
          >
            Delete
          </button>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-primary-full"
            disabled={busy || !name.trim()}
            onClick={() => void run(() => api.changeCategoryGroup(group.id, { name: name.trim(), forResale }))}
          >
            {busy ? <Working label="Saving" /> : "Save"}
          </button>
        </>
      }
    >
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <div className="grid grid-cols-12 gap-4">
        <Field id="edit-group-name" label="Group name" className="col-span-12">
          <input id="edit-group-name" type="text" className="form-control" maxLength={60} value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        {group.direction === "money_out" ? (
          <label className="col-span-12 flex items-start gap-2 text-[0.8125rem]">
            <input type="checkbox" className="form-check-input mt-0.5" checked={forResale} onChange={(event) => setForResale(event.target.checked)} />
            <span>Stock bought to sell. Purchases under these categories show on Inventory purchases, not on Expenses.</span>
          </label>
        ) : null}
        <p className="col-span-12 text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-0">
          A group with categories under it cannot be deleted. Move them to another group first.
        </p>
      </div>
    </Modal>
  );
}

/**
 * The transactions under a category, shown while the pointer rests on its count (D, 15
 * October 2026: "allow the transactions to show when I hover mouse over it").
 */
function EntriesPeek({ category }: { readonly category: Category }): React.ReactElement {
  const [at, setAt] = useState<{ left: number; top: number } | undefined>();
  const [entries, setEntries] = useState<CategoryEntry[] | undefined>();
  const timer = useRef<number | undefined>(undefined);
  const count = category.entryCount ?? category.useCount;
  const label = `${count} ${count === 1 ? "transaction" : "transactions"}`;
  if (count === 0) return <>{label}</>;
  const WIDTH = 560;
  return (
    <span
      className="underline decoration-dotted cursor-default"
      onMouseEnter={(event) => {
        const box = event.currentTarget.getBoundingClientRect();
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => {
          setAt({ left: Math.max(8, box.right - WIDTH), top: Math.min(box.bottom + 6, window.innerHeight - 200) });
          if (!entries) void api.categoryEntries(category.id).then((result) => setEntries(result.entries)).catch(() => setEntries([]));
        }, 200);
      }}
      onMouseLeave={() => {
        window.clearTimeout(timer.current);
        setAt(undefined);
      }}
    >
      {label}
      {at ? (
        <span
          role="tooltip"
          className="fixed z-[200] block rounded-md border border-defaultborder bg-white dark:bg-bodybg shadow-lg p-3 text-start whitespace-normal pointer-events-none overflow-hidden text-defaulttextcolor no-underline"
          style={{ left: at.left, top: at.top, width: WIDTH, maxHeight: window.innerHeight - at.top - 8 }}
        >
          <span className="block font-semibold mb-2">{category.name}</span>
          {!entries ? (
            <span className="block">Loading</span>
          ) : (
            <table className="w-full text-[0.8125rem]">
              <tbody>
                {entries.map((entry, index) => (
                  <tr key={index} className="border-b border-defaultborder">
                    <td className="py-1 pe-2 whitespace-nowrap align-top">{entry.day}</td>
                    <td className="py-1 pe-2 align-top">
                      {entry.description}
                      <span className="block text-[0.6875rem] text-[#8c9097] dark:text-white/50">{entry.reference}</span>
                    </td>
                    <td className={`py-1 text-end tabular-nums whitespace-nowrap align-top ${entry.amountCents > 0 ? "text-success" : ""}`}>
                      {entry.amountCents > 0 ? "+" : ""}
                      {formatMoney(entry.amountCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {entries && entries.length < count ? (
            <span className="block text-[0.75rem] text-[#8c9097] mt-1">The latest {entries.length} of {count}.</span>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}
